/* People — who can sign a station in to this salon.
 *
 * A tenant with no members cannot be used at all: `private.auth_tenant_id()`
 * reads this table, so an account with no row here has no tenant, every policy
 * evaluates to null, and the board comes up able to read nothing. That is the
 * failure this page exists to prevent and to fix.
 *
 * ── WHERE EACH ACTION GOES ──────────────────────────────────────────────────
 *
 * Split between the policies and the edge function, and the line is exactly
 * "does this need auth.users":
 *
 *   role change, remove   ordinary REST under the admin policy on
 *                         tenant_members. The row is already in hand.
 *   attach, invite, list  the function. `auth.users` is not reachable by
 *                         `authenticated` at any grant, so an email address
 *                         cannot become a user id in the browser.
 *
 * ── ROLES ───────────────────────────────────────────────────────────────────
 *
 * The menus offer this salon's own roles, from the Roles page
 * (15-roles.sql). A role decides which features somebody may use, in the
 * console and on the rows, so changing one here changes what that person can
 * see on their next query. `owner` additionally manages the salon's accounts
 * from the console (09-owner-members.sql).
 *
 * On a project without 15-roles.sql the menus fall back to owner and station,
 * which are then recorded and enforced nowhere beyond that.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { store } from '../store.js';
import { nav } from '../nav.js';
import { licensing } from '../rules/licensing.js';
import { ConfirmButton } from '../components/confirm-button.js';

var Members = {
  name: 'AdminMembers',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var members = Vue.ref([]);
    var users = Vue.ref([]);
    var loaded = Vue.ref(false);

    var attachEmail = Vue.ref('');
    var attachRole = Vue.ref('station');
    var newEmail = Vue.ref('');
    var newRole = Vue.ref('owner');
    var created = Vue.ref(null);   // { email, password, attached } — shown once
    var copied = Vue.ref(null);    // which field of it: 'email' | 'password'

    async function load() {
      var id = store.selectedId.value;
      if (!id) return;
      loaded.value = false;
      store.loadRoles(id, true);

      var rows = await api.rows('tenant_members', id, function (q) {
        return q.order('created_at');
      });
      if (rows) members.value = rows;
      // A salon's first account is its owner; after that, most are stations.
      newRole.value = members.value.length ? 'station' : 'owner';

      // Emails come from the function; the membership rows carry only ids. A
      // failure here is not fatal — the page still lists the memberships, with
      // ids where the addresses would be.
      var res = await api.call('users.list', { perPage: 1000 });
      if (res) users.value = res.users || [];

      loaded.value = true;
    }

    Vue.watch(store.selectedId, load, { immediate: true });

    var emailOf = Vue.computed(function () {
      var out = {};
      users.value.forEach(function (u) { out[u.id] = u; });
      return out;
    });

    var rows = Vue.computed(function () {
      return members.value.map(function (m) {
        var u = emailOf.value[m.user_id] || null;
        return {
          userId: m.user_id,
          role: m.role,
          createdAt: m.created_at,
          email: u ? u.email : null,
          lastSignInAt: u ? u.lastSignInAt : null,
          confirmed: u ? u.confirmed : null
        };
      });
    });

    /* Somebody attached to more than one salon.
     *
     * Worth surfacing loudly, because auth_tenant_id() does `limit 1` on this
     * table with no ORDER BY: a user in two tenants gets whichever row Postgres
     * hands back first, which is not a choice anybody made and can change
     * between queries. Support access — one person, several salons — is a
     * chosen-tenant claim that does not exist yet. Until it does, this is a
     * misconfiguration rather than a feature.
     */
    var multiTenant = Vue.ref([]);

    async function checkMultiTenant() {
      var ids = members.value.map(function (m) { return m.user_id; });
      if (!ids.length) { multiTenant.value = []; return; }
      var all = await api.query('Checking memberships', function (sb) {
        return sb.from('tenant_members').select('user_id, tenant_id').in('user_id', ids);
      });
      if (!all) return;
      var count = {};
      all.forEach(function (r) { count[r.user_id] = (count[r.user_id] || 0) + 1; });
      multiTenant.value = ids.filter(function (id) { return count[id] > 1; });
    }

    Vue.watch(members, checkMultiTenant);

    async function attach() {
      var id = store.selectedId.value;
      var email = attachEmail.value.trim();
      if (!email) return;
      var res = await api.call('members.attach', {
        tenantId: id, email: email, role: attachRole.value
      });
      if (!res) return;
      attachEmail.value = '';
      api.toast(email + ' can now sign in to this salon.');
      await load();
      store.loadTenants();
    }

    // The salon's roles, or the two that every salon had before roles existed.
    var roleOptions = Vue.computed(function () {
      if (store.roles.missing || store.roles.loadedFor !== store.selectedId.value) {
        return [{ id: 'station', name: 'Station' }, { id: 'owner', name: 'Owner' }];
      }
      return store.roles.rows;
    });

    // A member whose role this screen has no row for. Cannot happen with the
    // foreign key in place; shown rather than silently rendered as the first
    // option, which would make a role change look already made.
    function roleKnown(id) {
      return roleOptions.value.some(function (r) { return r.id === id; });
    }

    async function setRole(row, role) {
      var id = store.selectedId.value;
      var res = await api.updateRow('tenant_members', id, { user_id: row.userId }, { role: role });
      if (!res) return;
      var m = members.value.find(function (x) { return x.user_id === row.userId; });
      if (m) m.role = role;
      api.log(id, 'member.role', { user: row.userId, role: role });
    }

    async function detach(row) {
      var id = store.selectedId.value;
      var res = await api.deleteRows('tenant_members', id, { user_id: row.userId });
      if (!res) return;
      members.value = members.value.filter(function (m) { return m.user_id !== row.userId; });
      api.log(id, 'member.detach', { user: row.userId, email: row.email });
      api.toast('Removed. That account can no longer read anything of this salon’s.');
      store.loadTenants();
    }

    /* A new account is attached as part of making it. As a separate step it was
     * easy to miss, and an account with no membership signs in to no salon at
     * all: no tenant, no licence, no modules. A refusal here (the seat limit,
     * usually) leaves the account made and says so; api.call shows why. */
    async function attachNew(tenantId, email) {
      return !!(await api.call('members.attach', {
        tenantId: tenantId, email: email, role: newRole.value
      }));
    }

    async function invite() {
      var id = store.selectedId.value;
      var email = newEmail.value.trim();
      if (!email) return;
      var res = await api.call('users.invite', { email: email, tenantId: id });
      if (!res) return;
      newEmail.value = '';
      var attached = await attachNew(id, res.user.email);
      if (attached) api.toast('Invited. They choose their own password.');
      load();
      store.loadTenants();
    }

    async function createAccount() {
      var id = store.selectedId.value;
      var email = newEmail.value.trim();
      if (!email) return;
      var res = await api.call('users.create', { email: email, tenantId: id });
      if (!res) return;
      // Up before the attach is tried: this is the only copy of the password.
      copied.value = null;
      created.value = { email: res.user.email, password: res.password, attached: true };
      newEmail.value = '';
      created.value.attached = await attachNew(id, res.user.email);
      load();
      store.loadTenants();
    }

    function copyCreated(field) {
      if (!created.value || !navigator.clipboard) return;
      navigator.clipboard.writeText(created.value[field]).then(function () {
        copied.value = field;
      });
    }

    /* The seat limit, counted off the rows on this page rather than off the
     * tenant list's `member_count`.
     *
     * The list's count is a snapshot from the last time it was loaded, and this
     * page is where somebody adds the member that crosses the line — a limit
     * that only caught up on the next reload would let one through and then
     * report it as over. The edge function enforces it either way; this is so
     * the screen is not the thing that lied.
     */
    var seats = Vue.computed(function () {
      return licensing.seatState(store.entitlement.value.seats, rows.value.length);
    });

    return {
      rows: rows,
      loaded: loaded,
      seats: seats,
      licence: store.entitlement,
      multiTenant: multiTenant,
      attachEmail: attachEmail,
      attachRole: attachRole,
      roleOptions: roleOptions,
      roleKnown: roleKnown,
      attach: attach,
      setRole: setRole,
      detach: detach,
      newEmail: newEmail,
      newRole: newRole,
      invite: invite,
      createAccount: createAccount,
      created: created,
      copied: copied,
      copyCreated: copyCreated,
      stamp: store.stamp,
      ago: store.ago,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>People</h2>',
    '    <p class="page__lead">Accounts that can sign a station in to this salon. A tenant with',
    '      nobody here cannot be used: with no membership there is no tenant, and every table',
    '      the board reads comes back empty.</p>',
    '  </div>',
    '',
    '  <div v-if="created" class="card card--reveal">',
    '    <h3>Account created &mdash; here is the password, once.</h3>',
    '    <p class="hint">Generated on the server and never stored, so it cannot be shown again.</p>',
    '    <p v-if="!created.attached" class="warn-line">The account exists but could not be',
    '      attached to this salon, so it cannot use the console yet. Attach it below.</p>',
    '    <div class="reveal-field">',
    '      <span class="field__label">Username</span>',
    '      <p class="token mono">{{ created.email }}</p>',
    '      <button type="button" class="btn btn--primary btn--sm" @click="copyCreated(\'email\')">',
    '        {{ copied === "email" ? "Copied" : "Copy" }}</button>',
    '    </div>',
    '    <div class="reveal-field">',
    '      <span class="field__label">Password</span>',
    '      <p class="token mono">{{ created.password }}</p>',
    '      <button type="button" class="btn btn--primary btn--sm" @click="copyCreated(\'password\')">',
    '        {{ copied === "password" ? "Copied" : "Copy" }}</button>',
    '    </div>',
    '    <button type="button" class="btn btn--quiet btn--sm" @click="created = null">',
    '      I have saved it</button>',
    '  </div>',
    '',
    '  <p v-if="!loaded" class="empty">Loading…</p>',
    '  <p v-else-if="!rows.length" class="warn-line">Nobody is attached to this salon. Its',
    '    board cannot read or write anything until somebody is.</p>',
    '',
    '  <p v-if="loaded && seats.limit != null" class="filters__count">',
    '    <strong>{{ seats.label }}</strong> on their',
    '    <button type="button" class="link-btn" @click="goPage(\'licensing\')">licence</button>.',
    '    <span v-if="seats.over">More are attached than it allows &mdash; nobody is signed out by',
    '      that, but no further account can be attached until the limit is raised.</span>',
    '    <span v-else-if="seats.full">That is the last one. Attaching another is refused by the',
    '      server, not just by this page.</span>',
    '  </p>',
    '',
    '  <table v-if="rows.length" class="grid grid--plain">',
    '    <thead><tr><th>Account</th><th>Role</th><th>Last signed in</th><th>Member since</th>',
    '      <th></th></tr></thead>',
    '    <tbody>',
    '      <tr v-for="r in rows" :key="r.userId">',
    '        <td>',
    '          <span class="grid__name">{{ r.email || "(unknown account)" }}</span>',
    '          <span class="grid__id mono">{{ r.userId.slice(0, 8) }}</span>',
    '          <span v-if="multiTenant.includes(r.userId)" class="pill pill--bad"',
    '            title="This account belongs to more than one salon. auth_tenant_id() picks one arbitrarily.">',
    '            in several salons</span>',
    '        </td>',
    '        <td>',
    '          <select class="input input--slim" :value="r.role"',
    '            @change="setRole(r, $event.target.value)">',
    '            <option v-if="!roleKnown(r.role)" :value="r.role">{{ r.role }}</option>',
    '            <option v-for="o in roleOptions" :key="o.id" :value="o.id">{{ o.name }}</option>',
    '          </select>',
    '        </td>',
    '        <td :title="stamp(r.lastSignInAt)">{{ ago(r.lastSignInAt) }}</td>',
    '        <td :title="stamp(r.createdAt)">{{ ago(r.createdAt) }}</td>',
    '        <td>',
    '          <confirm-button label="Remove" size="sm"',
    '            confirm="Remove from this salon" @confirmed="detach(r)" />',
    '        </td>',
    '      </tr>',
    '    </tbody>',
    '  </table>',
    '',
    '  <p v-if="multiTenant.length" class="warn-line">One or more of these accounts belongs to',
    '    more than one salon. The tenant lookup takes the first row it finds with no ordering,',
    '    so which salon they get is not decided by anything &mdash; it is whichever row Postgres',
    '    returns. Give each salon its own accounts until support access exists.</p>',
    '',
    '  <div class="card">',
    '    <h3>Attach an existing account</h3>',
    '    <div class="card__row">',
    '      <input v-model="attachEmail" class="input" type="email" spellcheck="false"',
    '        placeholder="them@example.com" />',
    '      <label class="field">',
    '        <span class="field__label">Role</span>',
    '        <select v-model="attachRole" class="input">',
    '          <option v-for="o in roleOptions" :key="o.id" :value="o.id">{{ o.name }}</option>',
    '        </select>',
    '      </label>',
    '      <button type="button" class="btn btn--primary" @click="attach">Attach</button>',
    '    </div>',
    '    <p class="hint">The address must match an existing account exactly. What each role may',
    '      use is set on the <button type="button" class="link-btn" @click="goPage(\'roles\')">Roles</button>',
    '      page.</p>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <h3>Make a new account</h3>',
    '    <div class="card__row">',
    '      <input v-model="newEmail" class="input" type="email" spellcheck="false"',
    '        placeholder="them@example.com" />',
    '      <label class="field">',
    '        <span class="field__label">Role</span>',
    '        <select v-model="newRole" class="input">',
    '          <option v-for="o in roleOptions" :key="o.id" :value="o.id">{{ o.name }}</option>',
    '        </select>',
    '      </label>',
    '      <button type="button" class="btn btn--ghost" @click="invite">Send an invite</button>',
    '      <button type="button" class="btn btn--ghost" @click="createAccount">',
    '        Create with a password</button>',
    '    </div>',
    '    <p class="hint"><strong>Invite</strong> mails them a link and they choose their own',
    '      password &mdash; right for an owner, and it needs SMTP configured on the project.',
    '      <strong>Create</strong> generates a password and shows it once, which is what a front',
    '      desk tablet needs: there is no inbox at the desk to receive an invite at.</p>',
    '    <p class="hint">Either way the account is attached to this salon with the role chosen',
    '      here, and takes one of its seats.</p>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'members',
  name: 'People',
  group: 'Salon',
  page: Members
});

export { Members };
